import React, { useMemo } from 'react';

import { Box, Paper, Typography } from '@mui/material';

import { JSON_HIGHLIGHT_COLORS, parseJsonHighlights } from '~/utils/societePayloadBuilder/highlightJson';

import { PAYLOAD_BUILDER_MONO_FONT } from './PayloadBuilderConstants';

interface PayloadBuilderJsonPreviewProps<TState> {
  state: TState;
  serialize: (state: TState) => string;
  borderSoft: string;
  maxHeight: string;
}

export default function PayloadBuilderJsonPreview<TState>({
  state,
  serialize,
  borderSoft,
  maxHeight,
}: PayloadBuilderJsonPreviewProps<TState>) {
  const previewJson = useMemo(() => {
    try {
      return serialize(state);
    } catch {
      return '';
    }
  }, [serialize, state]);

  const segments = useMemo(() => (previewJson ? parseJsonHighlights(previewJson) : []), [previewJson]);

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'static',
        height: { xs: 'auto', md: maxHeight },
        maxHeight: { xs: 420, md: maxHeight },
        minHeight: { xs: 280, md: 360 },
        borderRadius: 2,
        border: `1px solid ${borderSoft}`,
        bgcolor: '#111827',
        color: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          fontFamily: PAYLOAD_BUILDER_MONO_FONT,
          fontSize: 12,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          position: 'relative',
        }}
      >
        {segments.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 120,
              color: 'grey.500',
            }}
          >
            <Typography variant="caption">Unable to render preview</Typography>
          </Box>
        ) : (
          <code>
            {segments.map((segment, index) =>
              segment.kind === 'plain' ? (
                <React.Fragment key={index}>{segment.text}</React.Fragment>
              ) : (
                <Box component="span" key={index} sx={{ color: JSON_HIGHLIGHT_COLORS[segment.kind] }}>
                  {segment.text}
                </Box>
              ),
            )}
          </code>
        )}
      </Box>
    </Paper>
  );
}
